// frontend/src/pages/booking/steps/DateTimeSelection.js
import React, { useState, useEffect } from 'react';
import API from '../../../services/api';

// ─── Timezone-safe helpers ────────────────────────────────────────────────────
// NEVER use new Date("YYYY-MM-DD") — it parses as UTC midnight and shifts dates
// in negative-offset timezones (North America). Always use local date parsing.

const getLocalTodayString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getLocalMonthString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

// Parse a "YYYY-MM" string into a local Date (1st of that month) — no UTC shift
const parseLocalMonth = (monthStr) => {
  const [year, month] = monthStr.split('-').map(Number);
  return new Date(year, month - 1, 1);
};

// Parse a "YYYY-MM-DD" string into a local Date — no UTC shift
const parseLocalDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Format a local Date object to "YYYY-MM"
const toMonthString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};
// ─────────────────────────────────────────────────────────────────────────────

const DateTimeSelection = ({ tour, onComplete }) => {
  // FIX 1: Use local month string instead of toISOString() which returns UTC
  const [selectedMonth, setSelectedMonth] = useState(getLocalMonthString());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tour && tour.id) {
      loadAvailability();
    }
  }, [tour, selectedMonth]);

  const loadAvailability = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await API.tours.getAvailability(tour.id, selectedMonth);
      setAvailability(response.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load availability');
      setAvailability([]);
    } finally {
      setLoading(false);
    }
  };

  // Group availability by date
  const availabilityByDate = availability.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleContinue = () => {
    if (selectedSlot) {
      onComplete({
        selectedDate: selectedSlot.date,
        selectedTime: selectedSlot.time_slot,
        selectedSlot: selectedSlot,
        availabilityId: selectedSlot.id,
        totalAmount: selectedSlot.price_single_atv || selectedSlot.price,
      });
    }
  };

  const handleMonthChange = (direction) => {
    // FIX 2: Parse as local date, mutate, then format back WITHOUT toISOString()
    // toISOString() converts to UTC which can shift the month backward in negative offsets
    const current = parseLocalMonth(selectedMonth);
    if (direction === 'next') {
      current.setMonth(current.getMonth() + 1);
    } else {
      current.setMonth(current.getMonth() - 1);
    }
    setSelectedMonth(toMonthString(current));
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  // Prevent navigating before the current month
  const isCurrentMonth = selectedMonth === getLocalMonthString();

  const generateCalendarDays = () => {
    const [year, month] = selectedMonth.split('-').map(Number);

    const firstDay = new Date(year, month - 1, 1);  // local, no UTC shift
    const lastDay  = new Date(year, month, 0);       // local, no UTC shift
    const daysInMonth    = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    // FIX 3: Compare date strings directly (lexicographic = chronological for ISO dates)
    // Old: new Date(dayObj.date) < new Date().setHours(0,0,0,0)
    //   → "YYYY-MM-DD" parses as UTC midnight → shifts dates backward in North America
    //   → future dates appear as "past" and get disabled
    // New: simple string compare, completely timezone-proof
    const todayStr = getLocalTodayString();

    const days = [];

    // Empty cells before the 1st of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({
        day,
        date: dateStr,
        hasAvailability: !!availabilityByDate[dateStr],
        isPast: dateStr < todayStr, // safe string compare — no timezone issues
      });
    }

    return days;
  };

  if (!tour) {
    return (
      <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
        <div className="text-center text-white">Loading tour information...</div>
      </div>
    );
  }

  const calendarDays = generateCalendarDays();

  // FIX 4: Use parseLocalMonth() for display — not new Date(str + '-01') which parses UTC
  const monthName = parseLocalMonth(selectedMonth).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-playfair font-bold text-white mb-2">
          Select Date & Time
        </h2>
        <p className="text-gray-400">
          Choose your preferred date and time slot for {tour.name}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">

        {/* ── Calendar ─────────────────────────────────────────────────────── */}
        <div>
          <div className="bg-gray-700 rounded-xl p-6">

            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => handleMonthChange('prev')}
                disabled={loading || isCurrentMonth}
                className="p-2 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <h3 className="text-xl font-bold text-white">{monthName}</h3>

              <button
                onClick={() => handleMonthChange('next')}
                disabled={loading}
                className="p-2 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Day Labels */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-center text-sm text-gray-400 font-semibold">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((dayObj, idx) => {
                  if (!dayObj) {
                    return <div key={`empty-${idx}`} className="aspect-square" />;
                  }

                  const isSelected = selectedDate === dayObj.date;
                  // isPast comes from safe string comparison inside generateCalendarDays()
                  const isDisabled = !dayObj.hasAvailability || dayObj.isPast;

                  return (
                    <button
                      key={dayObj.date}
                      onClick={() => !isDisabled && handleDateSelect(dayObj.date)}
                      disabled={isDisabled}
                      className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-emerald-600 text-white ring-2 ring-emerald-400'
                          : isDisabled
                          ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-600 text-white hover:bg-gray-500'
                      }`}
                    >
                      {dayObj.day}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Time Slots ───────────────────────────────────────────────────── */}
        <div>
          <div className="bg-gray-700 rounded-xl p-6">
            <h3 className="text-xl font-bold text-white mb-4">
              {selectedDate ? 'Available Times' : 'Select a date first'}
            </h3>

            {selectedDate && availabilityByDate[selectedDate] ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {availabilityByDate[selectedDate].map((slot) => (
                  <button
                    key={slot.id}
                    onClick={() => !slot.is_fully_booked && handleSlotSelect(slot)}
                    disabled={slot.is_fully_booked}
                    className={`w-full p-4 rounded-lg text-left transition-all ${
                      selectedSlot?.id === slot.id
                        ? 'bg-emerald-600 text-white ring-2 ring-emerald-400'
                        : slot.is_fully_booked
                        ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                        : 'bg-gray-600 text-white hover:bg-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-bold text-lg mb-1">
                          {slot.formatted_time || slot.time_slot}
                        </div>
                        <div className="text-xs">
                          {slot.is_fully_booked ? (
                            <span className="px-2 py-1 bg-red-500/20 rounded text-red-300">
                              Fully Booked
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-emerald-500/20 rounded text-emerald-300">
                              🏍️ {slot.atvs_available ?? slot.available_atv_capacity ?? 0} ATVs Available
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-400">From</div>
                        <div className="font-bold text-lg">
                          ${slot.price_single_atv || slot.price}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                {selectedDate
                  ? 'No available slots for this date.'
                  : 'Please select a date from the calendar.'}
              </div>
            )}
          </div>

          {/* Selected summary + Continue */}
          {selectedSlot && (
            <div className="mt-6">
              <div className="bg-gray-700 rounded-xl p-4 mb-4">
                <h4 className="text-white font-semibold mb-2">Selected:</h4>
                <p className="text-gray-300 text-sm">
                  📅{' '}
                  {parseLocalDate(selectedDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
                <p className="text-gray-300 text-sm mt-1">
                  ⏰ {selectedSlot.formatted_time || selectedSlot.time_slot}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Continue Button ───────────────────────────────────────────────── */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleContinue}
          disabled={!selectedSlot}
          className={`px-8 py-4 rounded-lg font-bold text-lg transition-all ${
            selectedSlot
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          }`}
        >
          Continue to Details →
        </button>
      </div>
    </div>
  );
};

export default DateTimeSelection;